import type { ReactNode } from "react";

import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useMinWidth } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// SessionColumns is the shared session workspace: capture panels on the
// left, the device (live stream or recording) in the middle, network
// requests on the right. The playground, the session page's live view and
// playback sections, and the signed-stream page all render through it so
// the pages look the same and stay that way.
//
// On wide screens the columns sit in a resizable group that stretches to
// the bottom of the viewport when it is the only content; below the
// breakpoint they stack, each with a usable minimum height. left and right
// are optional (the signed-stream page has no network column); when absent
// the center takes their share. left and right receive the height class
// matching the active layout; center sizes itself with h-full plus its own
// minimum.
export function SessionColumns({
	left,
	center,
	right,
	className,
}: {
	left?: (heightClassName: string) => ReactNode;
	center: ReactNode;
	right?: (heightClassName: string) => ReactNode;
	className?: string;
}) {
	const wideLayout = useMinWidth(1024);
	if (!wideLayout) {
		return (
			<div className={cn("flex flex-col gap-4", className)}>
				{left?.("min-h-[24rem]")}
				{center}
				{right?.("min-h-[16rem]")}
			</div>
		);
	}
	const centerSize = 100 - (left ? 25 : 0) - (right ? 33 : 0);
	return (
		<ResizablePanelGroup
			orientation="horizontal"
			className={cn(
				// max-h caps the group at the viewport: the pages that host it can
				// grow (min-h-screen roots), so without it the stream's intrinsic
				// height would push the whole workspace below the fold. flex-1
				// still stretches it to fill whatever the page has left when that
				// is less than the cap.
				"h-auto max-h-[90vh] min-h-[70vh] flex-1 gap-1",
				className,
			)}
		>
			{left && (
				<>
					<ResizablePanel defaultSize="25%" minSize="15%">
						{left("h-full")}
					</ResizablePanel>
					<ResizableHandle withHandle />
				</>
			)}
			<ResizablePanel defaultSize={`${centerSize}%`} minSize="25%">
				{center}
			</ResizablePanel>
			{right && (
				<>
					<ResizableHandle withHandle />
					<ResizablePanel defaultSize="33%" minSize="15%">
						{right("h-full")}
					</ResizablePanel>
				</>
			)}
		</ResizablePanelGroup>
	);
}
